/**
 * ConfirmDestructiveModal — Citadel-styled replacement for window.confirm /
 * window.alert across the consultant UI.
 *
 * Native confirm()/alert() dialogs are blocking, unstyled, OS-chrome popups
 * that break the Citadel Light visual language and (in some sandboxed shells)
 * behave inconsistently. This component renders the same decision inside the
 * canonical modal chassis (.modal-overlay + .modal + ModalCoverBand) so every
 * destructive action reads as part of the product.
 *
 * Four danger tiers drive the cover-band tint + confirm-button styling:
 *   archive  — reversible state change (Archive client, deactivate member)
 *   delete   — single-record permanent delete (btn-danger)
 *   cascade  — multi-record permanent delete; requires type-to-confirm
 *   info     — former window.alert() — single OK button, no destructive action
 *
 * Keyboard: Escape always cancels; Enter confirms when the confirm button is
 * enabled (i.e. not gated by requireTypedName).
 *
 * Usage (the common pattern — hoist the deferred action into a small piece of
 * component state, then point onConfirm at it):
 *
 *   const [confirmOpen, setConfirmOpen] = useState(false);
 *   ...
 *   <button onClick={() => setConfirmOpen(true)}>Delete</button>
 *   <ConfirmDestructiveModal
 *     open={confirmOpen}
 *     danger="delete"
 *     title='Delete "Acme Corp"?'
 *     description="This cannot be undone."
 *     onConfirm={() => { setConfirmOpen(false); void doDelete(); }}
 *     onCancel={() => setConfirmOpen(false)}
 *   />
 */
import { useEffect, useRef, useState } from 'react';
import { ModalCoverBand } from './ModalCoverBand';

export interface ConfirmDestructiveModalProps {
  open: boolean;
  title: string;
  description?: string;
  /** Rendered in the body with mono `·` markers. Use for "this will delete X / Y / Z". */
  impactBullets?: string[];
  danger?: 'archive' | 'delete' | 'cascade' | 'info';
  /** cascade only — user must type this string exactly to enable the confirm button. */
  requireTypedName?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

// Cover-band tint per tier — the band background is set via inline style on a
// wrapper around ModalCoverBand (which reads --brand). We override --brand
// locally so the band picks up the danger colour without touching globals.
const BAND_BRAND: Record<NonNullable<ConfirmDestructiveModalProps['danger']>, string> = {
  archive: '#1a1f2e', // navy — reversible
  delete: '#7a1420', // deep crimson
  cascade: '#5e0f18', // darkest crimson — most severe
  info: '#1a1f2e', // navy — neutral notice
};

export function ConfirmDestructiveModal({
  open,
  title,
  description,
  impactBullets,
  danger = 'delete',
  requireTypedName,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: ConfirmDestructiveModalProps) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isInfo = danger === 'info';
  const needsTyped = danger === 'cascade' && !!requireTypedName;
  const typedOk = !needsTyped || typed === requireTypedName;
  const confirmEnabled = typedOk;

  // Reset the typed-confirmation field whenever the modal (re)opens, and focus
  // the type-to-confirm input so the keyboard lands in the right place.
  useEffect(() => {
    if (open) {
      setTyped('');
      if (needsTyped) {
        // defer to next frame so the input is mounted
        const id = requestAnimationFrame(() => inputRef.current?.focus());
        return () => cancelAnimationFrame(id);
      }
    }
  }, [open, needsTyped]);

  // Escape = cancel; Enter = confirm (when enabled). Bound at the document
  // level so it works regardless of focus within the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Enter') {
        // Don't hijack Enter inside a multiline control; the type-to-confirm
        // input is single-line so Enter-to-confirm is the right behaviour.
        if (confirmEnabled) {
          e.preventDefault();
          onConfirm();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, confirmEnabled, onConfirm, onCancel]);

  if (!open) return null;

  const defaultConfirmLabel = isInfo
    ? 'OK'
    : danger === 'archive'
      ? 'Archive'
      : 'Delete';
  const confirmBtnClass =
    danger === 'delete' || danger === 'cascade' ? 'btn btn-danger' : 'btn btn-primary';

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, padding: 0, overflow: 'hidden' }}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Local --brand override tints the cover band per danger tier. */}
        <div style={{ ['--brand' as string]: BAND_BRAND[danger] }}>
          <ModalCoverBand title={title} onClose={onCancel} />
        </div>

        <div className="modal-body" style={{ maxHeight: '60vh' }}>
          {description && (
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--serif)',
                fontSize: 14,
                lineHeight: 1.7,
                color: 'var(--text)',
                whiteSpace: 'pre-line',
              }}
            >
              {description}
            </p>
          )}

          {impactBullets && impactBullets.length > 0 && (
            <ul
              style={{
                listStyle: 'none',
                margin: description ? '14px 0 0' : 0,
                padding: 0,
              }}
            >
              {impactBullets.map((b, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '4px 0',
                    fontFamily: 'var(--serif)',
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: 'var(--text)',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      fontFamily: 'var(--mono)',
                      color: 'var(--accent)',
                      flexShrink: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    ·
                  </span>
                  <span style={{ whiteSpace: 'pre-line' }}>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {needsTyped && (
            <div style={{ marginTop: 16 }}>
              <label
                style={{
                  display: 'block',
                  fontFamily: 'var(--mono)',
                  fontSize: 10,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                  marginBottom: 6,
                }}
              >
                Type{' '}
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)', textTransform: 'none', letterSpacing: 0 }}>
                  {requireTypedName}
                </span>{' '}
                to confirm
              </label>
              <input
                ref={inputRef}
                className="form-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                aria-label={`Type ${requireTypedName} to confirm`}
                style={{ fontFamily: 'var(--mono)', width: '100%' }}
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!isInfo && (
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              {cancelLabel ?? 'Cancel'}
            </button>
          )}
          <button
            type="button"
            className={confirmBtnClass}
            onClick={onConfirm}
            disabled={!confirmEnabled}
          >
            {confirmLabel ?? defaultConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
